import { useEffect, useRef, useState } from "react";
import useAxios from "../../../../../hooks/useAxios";
import { useTracking } from "react-tracking";
import {
  resolveClientAdministrationById,
  TOOLS,
} from "../../../../../shared/lib/routesResolver";
import {
  CLIENTS_ADMINISTRATION,
  CLIENTS_ADMINISTRATION_BY_ID,
} from "../../../../../shared/lib/apiEndpointsResolver";
import { catchFunction } from "../../../../../shared/lib/utils";
import SearchInput from "../../../../../shared/components/input/SearchInput";
import CreateClientDialog from "./create/CreateClientDialog";
import OverviewTabContent from "../../../../../shared/components/overview-panel/OverviewTabContent";
import ClientsAdministrationTable from "./ClientsAdministrationTable";
import OverviewComponent from "../../../../../shared/components/overview-panel/OverviewComponent";
import { useNavigate } from "react-router-dom";
import useToastWrapper from "../../../../../hooks/useToastWrapper";
import { useTranslation } from "react-i18next";
import useConfirmDialog from "../../../../../shared/components/confirm-dialog/useConfirmDialog";

const ClientsAdministrationOverview = () => {
  const axios = useAxios();
  const clientsRef = useRef([]);
  const [clients, setClients] = useState([]);
  const { trackEvent } = useTracking();
  const navigate = useNavigate();
  const toast = useToastWrapper();
  const { t } = useTranslation();
  const deleteClientDialog = useConfirmDialog();
  const [clientForDeletion, setClientForDeletion] = useState();

  const breadcrumbItems = [
    { title: "clients.overview.breadcrumb.first", link: TOOLS },
    { title: "clients.overview.breadcrumb.second" },
  ];

  const findClients = () => {
    axios
      .get(CLIENTS_ADMINISTRATION)
      .then((response) => {
        clientsRef.current = response.data;
        setClients(response.data);
      })
      .catch(catchFunction);
  };

  const filterClientsByName = (changeEvent) => {
    const searchTerm = changeEvent.target.value;
    setClients(
      clientsRef.current.filter((client) =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  };

  const deleteClient = () => {
    axios
      .delete(CLIENTS_ADMINISTRATION_BY_ID(clientForDeletion.id))
      .then((_) => {
        toast.success(t("clients.delete.success"));
        findClients();
      });
  };

  const onDeleteClientClick = (id) => {
    const client = clientsRef.current.filter((client) => client.id === id)[0];
    setClientForDeletion(client);
    deleteClientDialog.display(
      t("clients.delete.confirmation", { name: client.name }),
    );
  };

  const editClient = (id, formValues) => {
    axios
      .put(CLIENTS_ADMINISTRATION_BY_ID(id), formValues)
      .then((response) => {
        toast.success(
          t("clients.updateForm.success", { name: response.data.name }),
        );
        findClients();
      })
      .catch(catchFunction);
  };

  const onClientCreation = (formValues) => {
    axios
      .post(CLIENTS_ADMINISTRATION, formValues)
      .then((response) => {
        const data = response.data;
        toast.success(t("clients.createForm.success", { name: data.name }));
        navigate(resolveClientAdministrationById(data.id), {
          state: { id: data.id },
        });
      })
      .catch(catchFunction);
  };

  const OverviewOptions = (
    <>
      <SearchInput
        onChangeHandler={filterClientsByName}
        placeholder="clients.overview.searchInputPlaceholder"
      />
      <CreateClientDialog onSubmitHandler={onClientCreation} />
    </>
  );

  const clientsTabs = [
    {
      trigger: { value: "all", label: "clients.overview.tabs.trigger.all" },
      content: (
        <OverviewTabContent
          value="all"
          title="clients.overview.card.all.title"
          description="clients.overview.card.all.description"
        >
          <ClientsAdministrationTable
            data={clients}
            onDelete={onDeleteClientClick}
            onEdit={editClient}
          />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "enabled",
        label: "clients.overview.tabs.trigger.enabled",
      },
      content: (
        <OverviewTabContent
          value="enabled"
          title="clients.overview.card.enabled.title"
          description="clients.overview.card.enabled.description"
        >
          <ClientsAdministrationTable
            data={clients.filter((x) => x.enabled === true)}
            onDelete={onDeleteClientClick}
          />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "disabled",
        label: "clients.overview.tabs.trigger.disabled",
      },
      content: (
        <OverviewTabContent
          value="disabled"
          title="clients.overview.card.disabled.title"
          description="clients.overview.card.disabled.description"
        >
          <ClientsAdministrationTable
            data={clients.filter((x) => x.enabled === false)}
            onDelete={onDeleteClientClick}
          />
        </OverviewTabContent>
      ),
    },
  ];

  useEffect(() => {
    findClients();
    trackEvent({
      page: "clients-overview",
      event: "page-load",
    });
  }, []);

  return (
    <>
      {deleteClientDialog.renderSafeDialog(deleteClient)}
      <OverviewComponent
        breadcrumbItems={breadcrumbItems}
        options={OverviewOptions}
        tabs={clientsTabs}
        defaultTabValue="enabled"
      />
    </>
  );
};

export default ClientsAdministrationOverview;
